import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { useViewer } from '@/entities/viewer';
import {
  FeatureSection,
  FinalCta,
  Frame,
  HowItWorks,
  LandingFooter,
  LandingHeader,
  Overview,
  Shot,
  ViewSwitch,
} from '@/features/landing';

import styles from './HomePage.module.css';

/**
 * The public landing page (`/`): a header, the hero (what Planosfera is, beside the real board and
 * the real phone calendar), a three-sentence overview, one section per capability each shown by a
 * picture of the actual application, a short "how to start", the closing call to action and the
 * footer. The pictures are screenshots with invented data (`e2e/landing-assets.spec.ts`), never a
 * mock-up. It shows no account data and fetches nothing but the public service status.
 */
export function HomePage() {
  const { t, i18n } = useTranslation();
  const viewer = useViewer();
  const signedIn = viewer.status === 'authenticated';

  // The document's title and description follow the language (restored when leaving the page).
  useEffect(() => {
    const previousTitle = document.title;
    const meta = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    const previousDescription = meta?.content;
    document.title = t('landing.meta.title');
    if (meta) meta.content = t('landing.meta.description');
    return () => {
      document.title = previousTitle;
      if (meta && previousDescription !== undefined) meta.content = previousDescription;
    };
  }, [t, i18n.resolvedLanguage]);

  return (
    <div className={styles.page} id="top">
      <LandingHeader />
      <main className={styles.main}>
        <section className={styles.hero} aria-labelledby="landing-title">
          <div className={styles.copy}>
            <p className={styles.eyebrow}>{t('landing.hero.eyebrow')}</p>
            <h1 id="landing-title" className={styles.headline}>
              <span>{t('landing.hero.title1')}</span> <span>{t('landing.hero.title2')}</span>
            </h1>
            <p className={styles.lead}>{t('landing.hero.lead')}</p>
            <div className={styles.actions}>
              {signedIn ? (
                <Link to="/app" className={styles.primary}>
                  {t('landing.cta.open')}
                </Link>
              ) : (
                <>
                  <Link to="/register" className={styles.primary}>
                    {t('landing.cta.start')}
                  </Link>
                  <Link to="/login" className={styles.secondary}>
                    {t('landing.cta.signIn')}
                  </Link>
                </>
              )}
            </div>
            <p className={styles.note}>{t('landing.hero.note')}</p>
          </div>
          <div className={styles.visual}>
            <Frame>
              <Shot name="board" eager />
            </Frame>
            <div className={styles.phone}>
              <Frame phone>
                <Shot name="phone-calendar" phone eager />
              </Frame>
            </div>
          </div>
        </section>

        <Overview />
        <FeatureSection id="boards">
          <Frame>
            <Shot name="board" />
          </Frame>
        </FeatureSection>
        <FeatureSection id="calendar" flip>
          <Frame>
            <Shot name="calendar" />
          </Frame>
        </FeatureSection>
        <FeatureSection id="databases">
          <ViewSwitch />
        </FeatureSection>
        <FeatureSection id="teamwork" flip>
          <Frame>
            <Shot name="home" />
          </Frame>
        </FeatureSection>
        <HowItWorks />
        <FinalCta />
      </main>
      <LandingFooter />
    </div>
  );
}
